import {
  BriefcaseIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  LanguageIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import { format } from 'date-fns'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { getLanguage } from '../../constants/languages'
import { useNotificationContext } from '../../context'
import { getInviteToAssessmentLink } from '../../helpers'
import { toPickerDate } from '../../helpers/assessment-deadline'
import {
  buildInviteMessage,
  getInviteMessageLanguage
} from '../../helpers/invite-message'
import { useCopyToClipboard } from '../../hooks/use-copy-to-clipboard'
import { AnimatedCopyIcon, SuccessCheck } from '../motion'
import Button from '../tailwind/Button'
import SpaceInviteLink from './space-invite-link'

const I18N = 'components.career.invite_talent_modal.'

const MetaChip = ({ icon: Icon, text }) => (
  <span className='inline-flex max-w-full items-center gap-x-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700'>
    <Icon className='size-3.5 shrink-0 text-gray-500' aria-hidden='true' />
    <span className='truncate'>{text}</span>
  </span>
)

MetaChip.propTypes = {
  icon: PropTypes.elementType.isRequired,
  text: PropTypes.string.isRequired
}

// Stelle, Sprache und Frist der Einladung, auch für mehrere Einladungen
export const InviteMeta = ({ careerTitle, language, expiresAt }) => {
  const { t } = useTranslation()
  const expiresDate = toPickerDate(expiresAt)

  return (
    <div className='mt-4 flex flex-wrap gap-2'>
      <MetaChip
        icon={BriefcaseIcon}
        text={careerTitle || t('talents.talentpool_title')}
      />
      {language && (
        <MetaChip icon={LanguageIcon} text={t(getLanguage(language))} />
      )}
      {expiresDate && (
        <MetaChip
          icon={CalendarDaysIcon}
          text={t(I18N + 'meta_expires_at', {
            date: format(expiresDate, 'dd.MM.yyyy')
          })}
        />
      )}
    </div>
  )
}

InviteMeta.propTypes = {
  careerTitle: PropTypes.string,
  // kleingeschrieben, wie getLanguage sie erwartet
  language: PropTypes.string,
  expiresAt: PropTypes.string
}

InviteMeta.defaultProps = {
  careerTitle: undefined,
  language: undefined,
  expiresAt: undefined
}

// Statt des Hakens, wenn das Talent steht, die Mail aber nicht rausging
export const MailNotSentIcon = () => (
  <span
    aria-hidden='true'
    className='flex size-12 shrink-0 items-center justify-center rounded-full bg-yellow-100'
  >
    <ExclamationTriangleIcon className='size-6 text-yellow-600' />
  </span>
)

// Erfolgs-Step nach „Talent einladen“, gleicher Aufbau für E-Mail und Link.
// Er sagt, was erledigt ist, für wen, und was als Nächstes zu tun ist. Beim
// Link ist Kopieren die Hauptaktion und hat den Fokus, Enter kopiert also.
// Ging die Mail nicht raus, steht das Talent trotzdem. Dann ist „E-Mail
// erneut senden“ die Hauptaktion, der Link bleibt zum Selbst-Teilen.
const InviteSuccess = ({
  via,
  space,
  invitee,
  careerTitle,
  mailNotSent,
  isResending,
  onResend,
  onInviteAnother,
  onDone
}) => {
  const { t, i18n } = useTranslation()
  const { error } = useNotificationContext()
  const fallbackCopy = useCopyToClipboard()

  const language = (space.language || '').toLowerCase()
  const name = [invitee.firstname, invitee.lastname]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')

  const inviteLink = getInviteToAssessmentLink({ space_id: space.id, language })

  const message = useMemo(() => {
    if (via !== 'LINK') return undefined

    const messageLanguage = getInviteMessageLanguage(language)

    return buildInviteMessage({
      t: i18n.getFixedT(messageLanguage),
      language: messageLanguage,
      link: inviteLink,
      firstname: invitee.firstname,
      careerTitle,
      expiresAt: space.expiresAt
    })
  }, [
    via,
    i18n,
    language,
    inviteLink,
    space.expiresAt,
    invitee.firstname,
    careerTitle
  ])

  // Fallback nach E-Mail-Einladungen, falls die Mail im Spam landet oder das
  // Talent zusätzlich per Messenger erinnert werden soll
  const copyFallbackLink = async () => {
    if (!(await fallbackCopy.copy(inviteLink))) {
      error(t(I18N + 'copy_error'))
    }
  }

  const linkTitle = name
    ? t(I18N + 'success_link_title_with_name', { name })
    : t(I18N + 'success_link_title')
  const emailTitle = t(
    I18N +
      (mailNotSent ? 'success_email_not_sent_title' : 'success_email_title')
  )
  const title = via === 'EMAIL' ? emailTitle : linkTitle

  const description =
    via === 'EMAIL'
      ? t(
          I18N +
            (mailNotSent
              ? 'success_email_not_sent_description'
              : 'success_email_description'),
          { email: invitee.email }
        )
      : t(I18N + 'success_link_description')

  return (
    <div>
      {mailNotSent ? <MailNotSentIcon /> : <SuccessCheck />}
      <h2 className='mt-4 text-2xl font-extrabold tracking-tight text-gray-900'>
        {title}
      </h2>
      <p className='mt-2 text-sm text-gray-600'>{description}</p>
      <InviteMeta
        careerTitle={careerTitle}
        language={language}
        expiresAt={space.expiresAt}
      />
      {via === 'EMAIL' && (
        <div className='mt-5 flex flex-wrap items-center gap-x-4 gap-y-3'>
          {mailNotSent && (
            <Button.SecondarySM
              autoFocus
              text={t(I18N + 'resend_email_action')}
              isLoading={isResending}
              onClick={onResend}
            />
          )}
          <Button.Text
            minWidth=''
            icon={<AnimatedCopyIcon copied={fallbackCopy.copied} />}
            text={t(
              I18N +
                (fallbackCopy.copied
                  ? 'copy_link_success'
                  : 'copy_invite_link_fallback')
            )}
            onClick={copyFallbackLink}
          />
        </div>
      )}
      {via === 'LINK' && (
        <div className='mt-6'>
          <SpaceInviteLink
            spaceID={space.id}
            language={space.language}
            message={message}
            autoFocus
          />
        </div>
      )}
      <div className='mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-gray-100 pt-5'>
        <Button.Text
          minWidth=''
          icon={<PlusIcon className='size-4' aria-hidden='true' />}
          text={t(I18N + 'invite_another_action')}
          onClick={onInviteAnother}
          disabled={isResending}
        />
        <Button.SecondaryXL
          autoFocus={via === 'EMAIL' && !mailNotSent}
          text={t(I18N + 'done_action')}
          onClick={onDone}
          disabled={isResending}
        />
      </div>
    </div>
  )
}

InviteSuccess.propTypes = {
  via: PropTypes.oneOf(['EMAIL', 'LINK']).isRequired,
  space: PropTypes.shape({
    id: PropTypes.string.isRequired,
    language: PropTypes.string,
    expiresAt: PropTypes.string
  }).isRequired,
  invitee: PropTypes.shape({
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    email: PropTypes.string
  }),
  careerTitle: PropTypes.string,
  // Talent angelegt, aber die Einladungs-Mail ging nicht raus
  mailNotSent: PropTypes.bool,
  isResending: PropTypes.bool,
  onResend: PropTypes.func,
  onInviteAnother: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired
}

InviteSuccess.defaultProps = {
  invitee: {},
  careerTitle: undefined,
  mailNotSent: false,
  isResending: false,
  onResend: () => {}
}

export default InviteSuccess
